import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import MapPage from './pages/MapPage';
import HistoryPage from './pages/HistoryPage';
import AlertsPage from './pages/AlertsPage';
import AdminPage from './pages/AdminPage';
import FrotaPage from './pages/FrotaPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F7F5F0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#7A7770', fontFamily: "'Nunito', ui-sans-serif, system-ui, sans-serif", fontWeight: 600, fontSize: 14 }}>
          <svg style={{ width: 20, height: 20, animation: 'spin 1s linear infinite' }} fill="none" viewBox="0 0 24 24">
            <circle style={{ opacity: 0.2 }} cx="12" cy="12" r="10" stroke="#00955F" strokeWidth="4" />
            <path fill="#00955F" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Carregando...
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <WebSocketProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                element={
                  <RequireAuth>
                    <Layout />
                  </RequireAuth>
                }
              >
                <Route index element={<Navigate to="/map" replace />} />
                <Route path="/map" element={<MapPage />} />
                <Route path="/frota" element={<FrotaPage />} />
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/alerts" element={<AlertsPage />} />
                <Route path="/admin" element={<AdminPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/map" replace />} />
            </Routes>
          </WebSocketProvider>
        </AuthProvider>
      </BrowserRouter>

      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#FFFFFF',
            color: '#0F0E0C',
            border: '1px solid rgba(15,14,12,0.08)',
            borderRadius: '12px',
            boxShadow: '0 2px 4px rgba(15,14,12,0.06), 0 8px 20px rgba(15,14,12,0.08)',
            fontFamily: "'Nunito', ui-sans-serif, system-ui, sans-serif",
            fontWeight: 600,
            fontSize: '13px',
          },
          success: {
            iconTheme: { primary: '#00955F', secondary: '#E0F1E8' },
          },
          error: {
            iconTheme: { primary: '#D32020', secondary: '#FADEDE' },
          },
        }}
      />
    </QueryClientProvider>
  );
}
